import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { DataSurfacePage } from '../../../features/surface/DataSurfacePage';
import { findMenuItemByLink, findParentPathMenuItem } from '../../../features/menu/helpers';
import { store } from '../../../app/store';
import { menuApi } from '../../../features/menu/api/menuApi';
import { isFinanceEmailHistoryId } from '../../../features/grid/utils/financeEmailHistoryId';

// Search params schema for state that was previously in location.state
const detailSearchSchema = z.object({
  gridContentKey: z.string().optional(),
  detailFormKey: z.string().optional(),
  bc: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  DetailedData: z.string().optional(),
});

// Params validation - numeric, finance email history keys (o123 / i456), or GUID
const validateId = (id: string): string => {
  const isNumericId = /^\d+$/.test(id);
  const isGuidId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

  if (!isNumericId && !isFinanceEmailHistoryId(id) && !isGuidId) {
    throw new Error('Invalid ID format');
  }

  return id;
};

export const Route = createFileRoute('/_protected/$/$id')({
  validateSearch: detailSearchSchema,
  parseParams: (params) => ({
    id: validateId(params.id),
  }),
  beforeLoad: async ({ location }) => {
    // Extract base path from the URL (remove the ID segment)
    const pathname = location.pathname;
    const segments = pathname.split('/').filter(Boolean);
    segments.pop(); // Remove the ID
    const basePath = '/' + segments.join('/');

    // Get menu items from RTK Query cache
    const state = store.getState();
    const cacheKey = menuApi.endpoints.getMenuItems.select(undefined)(state);
    const menuItems = cacheKey.data || [];

    if (menuItems.length === 0) {
      // Menu not loaded yet - allow navigation to proceed
      return { menuItem: null, basePath, mode: 'edit' as const };
    }

    // Find menu item for this page
    let menuItem = findMenuItemByLink(menuItems, basePath);

    // Fallback for child grids
    if (!menuItem) {
      menuItem = findParentPathMenuItem(menuItems, basePath);
    }

    // If no match, allow it anyway (DetailPage will handle the error)
    if (!menuItem) {
      return { menuItem: null, basePath, mode: 'edit' as const };
    }

    return { menuItem, basePath, mode: 'edit' as const };
  },
  component: DataSurfacePage,
});
